import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { label } from "@/lib/format";
import { Panel } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

async function saveSetting(key, value, refresh) {
  try {
    await api.put(`/settings/${key}`, { value });
    toast.success("Saved");
    refresh();
  } catch (e) {
    toast.error(errMsg(e));
  }
}

function ListEditor({ k, title, hint }) {
  const { meta, refreshMeta } = useAuth();
  const [text, setText] = useState("");
  useEffect(() => setText((meta.settings[k] || []).join("\n")), [meta, k]);
  const list = text.split("\n").map((s) => s.trim()).filter(Boolean);
  return (
    <Panel title={title} testid={`list-${k}`} action={<Button size="sm" onClick={() => saveSetting(k, list, refreshMeta)} className="bg-orange-500 hover:bg-orange-600" data-testid={`save-${k}`}>Save</Button>}>
      <Textarea rows={8} value={text} onChange={(e) => setText(e.target.value)} data-testid={`textarea-${k}`} />
      <p className="text-xs text-slate-400 mt-2">{hint}</p>
    </Panel>
  );
}

export default function ListsTab() {
  const { meta, refreshMeta, can } = useAuth();
  const [threshold, setThreshold] = useState(meta.settings.quotation_approval_threshold ?? 0);
  const [kpis, setKpis] = useState(meta.settings.dashboard_kpis || []);
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <ListEditor k="lead_sources" title="Lead & campaign sources" hint="One per line. Used for lead entry, filters and source performance reports. Add campaign names here (e.g. 'Facebook – June Promo')." />
      {can("settings.manage") && (
        <>
          <ListEditor k="lost_reasons" title="Lost reasons" hint="One per line. Required whenever a lead is moved to a lost stage." />
          <Panel title="Quotation approval rule" testid="approval-threshold-panel" action={<Button size="sm" onClick={() => saveSetting("quotation_approval_threshold", Number(threshold), refreshMeta)} className="bg-orange-500 hover:bg-orange-600" data-testid="save-threshold">Save</Button>}>
            <div className="eyebrow mb-1.5">Approval required at or above (PHP)</div>
            <Input type="number" min="0" value={threshold} onChange={(e) => setThreshold(e.target.value)} data-testid="threshold-input" />
            <p className="text-xs text-slate-400 mt-2">0 = every quotation needs approval before it can be marked sent. Approval is never automatic.</p>
          </Panel>
          <Panel title="Dashboard KPI cards" testid="dashboard-kpi-panel" action={<Button size="sm" onClick={() => saveSetting("dashboard_kpis", kpis, refreshMeta)} className="bg-orange-500 hover:bg-orange-600" data-testid="save-kpis">Save</Button>}>
            <div className="grid sm:grid-cols-2 gap-2">
              {meta.kpi_keys.map((k) => (
                <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={kpis.includes(k)} onCheckedChange={() => setKpis(kpis.includes(k) ? kpis.filter((x) => x !== k) : [...kpis, k])} data-testid={`kpi-toggle-${k}`} />{label(k)}</label>
              ))}
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
